import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api.js';

interface IntegrationStep {
  slug: string;
  label: string;
  fields: { name: string; placeholder: string; required: boolean }[];
  optional: boolean;
}

const STEPS: IntegrationStep[] = [
  {
    slug: 'openai',
    label: 'OpenAI (GPT-5, embeddings)',
    fields: [{ name: 'apiKey', placeholder: 'sk-...', required: true }],
    optional: true,
  },
  {
    slug: 'google',
    label: 'Google AI (Gemini)',
    fields: [{ name: 'apiKey', placeholder: 'AIza...', required: true }],
    optional: true,
  },
  {
    slug: 'openrouter',
    label: 'OpenRouter (catálogo dinâmico de modelos)',
    fields: [{ name: 'apiKey', placeholder: 'sk-or-...', required: true }],
    optional: true,
  },
  {
    slug: 'mercadopago',
    label: 'Mercado Pago (billing BR)',
    fields: [
      { name: 'accessToken', placeholder: 'APP_USR-...', required: true },
      { name: 'publicKey', placeholder: 'APP_USR-...', required: true },
      { name: 'webhookSecret', placeholder: 'opcional', required: false },
    ],
    optional: true,
  },
  {
    slug: 'resend',
    label: 'Resend (emails transacionais)',
    fields: [{ name: 'apiKey', placeholder: 're_...', required: true }],
    optional: true,
  },
];

export function BootstrapWizard() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  async function saveAndAdvance(skip: boolean) {
    setError(null);
    if (!skip && step) {
      const secret: Record<string, string> = {};
      for (const f of step.fields) {
        const v = values[`${step.slug}_${f.name}`] ?? '';
        if (f.required && !v) {
          setError(`Campo ${f.name} obrigatório`);
          return;
        }
        if (v) secret[f.name] = v;
      }
      if (Object.keys(secret).length > 0) {
        setSaving(true);
        try {
          await api(`/admin/integrations/${step.slug}`, {
            method: 'PUT',
            body: { label: step.label, secret },
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'erro ao salvar');
          setSaving(false);
          return;
        }
        setSaving(false);
      }
    }

    if (isLast) {
      try {
        await api('/admin/bootstrap/complete', { method: 'POST' });
        navigate({ to: '/' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'erro');
      }
    } else {
      setStepIdx((i) => i + 1);
    }
  }

  if (!step) return null;

  return (
    <main className="min-h-dvh px-6 py-12">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-semibold text-cyan-400 mb-1">Setup inicial</h1>
        <p className="text-zinc-500 text-sm mb-6">
          Configure as chaves de integração. Tudo cifrado AES-256-GCM no DB com sua master.key.
          Pode pular e cadastrar depois em <code>/admin/integrations</code>.
        </p>
        <div className="text-xs text-zinc-500 mb-4">
          Passo {stepIdx + 1} de {STEPS.length}
        </div>

        <div className="rounded-lg border border-zinc-800 p-5 bg-zinc-900/50">
          <h2 className="text-lg font-medium mb-4">{step.label}</h2>
          <div className="space-y-3">
            {step.fields.map((f) => (
              <input
                key={f.name}
                type="password"
                placeholder={f.placeholder}
                value={values[`${step.slug}_${f.name}`] ?? ''}
                onChange={(e) =>
                  setValues({ ...values, [`${step.slug}_${f.name}`]: e.target.value })
                }
                className="w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm focus:outline-none focus:border-cyan-500"
              />
            ))}
          </div>
          {error && <div className="text-rose-400 text-sm mt-3">{error}</div>}
        </div>

        <div className="flex gap-2 mt-6">
          {step.optional && (
            <button
              type="button"
              onClick={() => saveAndAdvance(true)}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:bg-zinc-900"
            >
              Pular
            </button>
          )}
          <button
            type="button"
            onClick={() => saveAndAdvance(false)}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-cyan-500 text-zinc-950 font-medium hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? 'salvando…' : isLast ? 'Concluir' : 'Salvar e continuar'}
          </button>
        </div>
      </div>
    </main>
  );
}
